import type { PensionPlannerInputs, PensionPlannerResult, PensionProjection } from './types';

export class PensionCalculator {
    // Maximum tax-free lump sum percentage (UK SIPP rules)
    private static readonly MAX_TAX_FREE_LUMP_SUM_PERCENTAGE = 25;

    static calculate(inputs: PensionPlannerInputs): PensionPlannerResult {
        // Validate inputs
        this.validateInputs(inputs);

        // Calculate annual contribution based on frequency
        const annualContribution = inputs.contributionFrequency === 'monthly'
            ? inputs.contributionAmount * 12
            : inputs.contributionAmount;

        // Convert percentages to decimals
        const annualReturnRate = inputs.expectedAnnualReturn / 100;
        const inflationRate = inputs.expectedAnnualInflation / 100;

        // Initialize tracking variables
        let currentValue = inputs.currentValue;
        let totalContributions = inputs.currentValue; // Starting value counts as contribution
        const projections: PensionProjection[] = [];

        // Project for each year
        for (let year = 1; year <= inputs.yearsToProject; year++) {
            const age = inputs.currentAge + year;

            // Add contributions at start of year
            currentValue += annualContribution;
            totalContributions += annualContribution;

            // Apply investment growth
            currentValue *= (1 + annualReturnRate);

            // Calculate real terms value (adjusted for inflation from current year)
            const realTermsValue = currentValue / Math.pow(1 + inflationRate, year);

            // Calculate investment growth (difference between current value and contributions)
            const investmentGrowth = currentValue - totalContributions;

            projections.push({
                year,
                age,
                nominalValue: currentValue,
                realTermsValue,
                totalContributions,
                investmentGrowth
            });
        }

        const finalProjection = projections[projections.length - 1];

        // Calculate tax-free lump sum (capped at 25%)
        const taxFreeLumpSumPercentage = Math.min(
            inputs.taxFreeLumpSumPercentage,
            this.MAX_TAX_FREE_LUMP_SUM_PERCENTAGE
        );

        const taxFreeLumpSumNominal = (finalProjection.nominalValue * taxFreeLumpSumPercentage) / 100;
        const taxFreeLumpSumReal = (finalProjection.realTermsValue * taxFreeLumpSumPercentage) / 100;

        const remainingPotNominal = finalProjection.nominalValue - taxFreeLumpSumNominal;
        const remainingPotReal = finalProjection.realTermsValue - taxFreeLumpSumReal;

        return {
            finalAge: finalProjection.age,
            finalNominalValue: finalProjection.nominalValue,
            finalRealTermsValue: finalProjection.realTermsValue,
            totalContributions: finalProjection.totalContributions,
            totalInvestmentGrowth: finalProjection.investmentGrowth,
            taxFreeLumpSumNominal,
            taxFreeLumpSumReal,
            remainingPotNominal,
            remainingPotReal,
            projections
        };
    }

    private static validateInputs(inputs: PensionPlannerInputs): void {
        if (inputs.currentValue < 0) {
            throw new Error('Current pension value cannot be negative');
        }

        if (inputs.contributionAmount < 0) {
            throw new Error('Contribution amount cannot be negative');
        }

        if (inputs.expectedAnnualReturn < -100) {
            throw new Error('Expected annual return cannot be less than -100%');
        }

        if (inputs.expectedAnnualReturn > 100) {
            throw new Error('Expected annual return cannot exceed 100%');
        }

        if (inputs.expectedAnnualInflation < -100) {
            throw new Error('Expected annual inflation cannot be less than -100%');
        }

        if (inputs.expectedAnnualInflation > 100) {
            throw new Error('Expected annual inflation cannot exceed 100%');
        }

        if (inputs.currentAge < 0 || inputs.currentAge > 150) {
            throw new Error('Current age must be between 0 and 150');
        }

        if (inputs.yearsToProject < 1 || inputs.yearsToProject > 100) {
            throw new Error('Years to project must be between 1 and 100');
        }

        if (inputs.taxFreeLumpSumPercentage < 0 || inputs.taxFreeLumpSumPercentage > 100) {
            throw new Error('Tax-free lump sum percentage must be between 0 and 100');
        }
    }
}
